import { useCallback, useEffect, useRef, useState } from "react";
import type { ReadSupportSurface } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type SupportSurfaceState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; surface: ReadSupportSurface };

export interface UseSupportSurfaceResult {
  state: SupportSurfaceState;
  // Re-runs the fetch without resetting to "loading", so a finished step
  // updates in place. Same shape as useHomeSurface.
  refetch: () => void;
}

export function useSupportSurface(): UseSupportSurfaceResult {
  const api = useApi();
  const [state, setState] = useState<SupportSurfaceState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchSurface = useCallback(() => {
    api.support
      .get()
      .then((surface) => {
        if (!cancelledRef.current) {
          setState({ status: "success", surface });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load support steps" });
        }
      });
  }, [api]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchSurface();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchSurface]);

  return { state, refetch: fetchSurface };
}
